import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ConsentStateUpdated, PageLoad } from 'src/services/personal-data-protection/actions/actions';
import { PersonalDataProtectionService } from 'src/services/personal-data-protection/services/personal-data-protection.service';

import { AnalyticsService } from '../analytics/analytics.service';
import { EnvironmentService } from '../environment/environment.service';
import { ScriptTemplateService } from '../script-template/script-template.service';
import { TRUSTARC_CONSENT_CATEGORIES } from '../trust-arc/trust-arc.service';
import { UserCentricsService } from '../user-centrics/user-centrics.service';

@Injectable({
    providedIn: 'root',
})
export class ConsentManagementInitializerService {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly translateService = inject(TranslateService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly userCentricsService = inject(UserCentricsService);
    private readonly personalDataProtectionService = inject(PersonalDataProtectionService);
    private readonly scriptTemplateService = inject(ScriptTemplateService);
    private readonly environmentService = inject(EnvironmentService);

    private readonly USERCENTRICS_SELECTOR = `[id="usercentrics-cmp"]`;

    public init(): void {
        this.initializeConsentManagement();
        this.setupConsentSubscriptions();
    }

    private initializeConsentManagement(): void {
        this.initializeUserCentrics();
        this.personalDataProtectionService.applyAction(new PageLoad());
    }

    private initializeUserCentrics(): void {
        this.scriptTemplateService
            .applyScriptTemplate(this.USERCENTRICS_SELECTOR, {
                'data-ruleset-id': this.environmentService.environment.userCentricsRulesetId,
                'data-language': this.translateService.getCurrentLang(),
            })
            .then(() => this.personalDataProtectionService.applyAction(new PageLoad()));

        if (isPlatformBrowser(this.platformId)) {
            this.analyticsService.init();
            this.userCentricsService.changeConsentState$.subscribe((state) => {
                this.personalDataProtectionService.applyAction(new ConsentStateUpdated(state));
            });
        }
    }

    private setupConsentSubscriptions(): void {
        this.personalDataProtectionService.consent$.subscribe((state) => {
            const hasFunctionalCookieConsent = Boolean(state[TRUSTARC_CONSENT_CATEGORIES.Functional]);
            this.analyticsService.setMarketoOptOut(!hasFunctionalCookieConsent);
        });
    }
}
